import { Button } from "@9amhealth/shared";
import styled from "@emotion/styled";
import { IonContent, IonModal } from "@ionic/react";
import { useWindowSize } from "@react-hooks-library/core";
import React, { FC, useEffect, useRef, useState } from "react";
import { tracker } from "src/state/state";
import Translate from "src/ui/components/Translate/Translate";

const SheetContent = styled.div`
  padding: 2rem 1.6rem calc(var(--safe-area-inset-bottom) + 3rem);
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Title = styled.h2`
  font-family: var(--font-family);
  font-weight: 500;
  font-size: 1.25rem;
  line-height: 1.4;
  letter-spacing: -0.02em;
  color: var(--greys-charcoal);
`;

const BodyText = styled.p`
  font-family: var(--font-family);
  font-weight: 400;
  font-size: 1rem;
  line-height: 1.4;
  letter-spacing: -0.02em;
  color: var(--greys-charcoal);
  margin: 0;
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 1.2rem;
  width: 100%;
  margin-top: 1rem;
`;

interface SkipOrScheduleSheetProps {
  isOpen: boolean;
  onClose: () => void;
  onSkip: () => void;
  onContinue: () => void;
}

export const SkipOrScheduleSheet: FC<SkipOrScheduleSheetProps> = ({
  isOpen,
  onClose,
  onSkip,
  onContinue
}) => {
  const modal = useRef<HTMLIonModalElement>(null);
  const sheetContentRef = useRef<HTMLDivElement>(null);
  const [breakpoint, setBreakpoint] = useState(0);
  const { height } = useWindowSize();
  const checkHeightInterval = useRef<ReturnType<typeof setInterval> | null>(
    null
  );

  const checkHeight = () => {
    if (modal.current && sheetContentRef.current) {
      const requiredHeightPercentage = Math.min(
        sheetContentRef.current.clientHeight / height,
        0.75
      );
      setBreakpoint(requiredHeightPercentage);
    }
  };

  useEffect(() => {
    if (isOpen) {
      tracker.track("Optional Coaching Call Information Dialog Opened");
      checkHeightInterval.current = setInterval(checkHeight, 100);
    } else {
      if (checkHeightInterval.current) {
        clearInterval(checkHeightInterval.current);
      }
    }

    return () => {
      if (checkHeightInterval.current) {
        clearInterval(checkHeightInterval.current);
      }
    };
  }, [isOpen]);

  return (
    <IonModal
      ref={modal}
      isOpen={isOpen}
      onDidDismiss={onClose}
      initialBreakpoint={breakpoint}
      breakpoints={[0, breakpoint]}
      key={breakpoint}
      backdropDismiss={true}
    >
      <IonContent scrollY={false}>
        <SheetContent ref={sheetContentRef}>
          <Title>
            <Translate msg="skipOrScheduleSheet.title" />
          </Title>
          <BodyText>
            <Translate msg="skipOrScheduleSheet.bodyText" />
          </BodyText>
          <ButtonContainer>
            <Button theme="charcoal" outline hideArrow grow onPress={onSkip}>
              <Translate msg="task.skip" />
            </Button>
            <Button theme="sunrise" grow onPress={onContinue}>
              <Translate msg="continue" />
            </Button>
          </ButtonContainer>
        </SheetContent>
      </IonContent>
    </IonModal>
  );
};
